/**
 * SessionJourneyModal Component
 * Mobile-responsive modal/drawer showing detailed user journey for a specific session
 */

import { useEffect, useState } from 'react';
import {
  X,
  Clock,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  MapPin,
  ExternalLink,
  FileText,
  Calendar,
  User,
  Tag,
  DollarSign,
  TrendingUp,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { formatDistanceToNow } from 'date-fns';

interface SessionJourneyModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string | null;
  companyId: string;
  dataSourceId: string;
}

interface JourneyStep {
  page_url: string;
  page_title: string;
  timestamp: string;
  time_on_page_seconds: number | null;
  referrer?: string;
}

interface SessionDetail {
  session_id: string;
  first_timestamp: string;
  last_timestamp: string;
  duration_seconds: number;
  total_pages: number;
  device_type: string;
  browser: string;
  os: string;
  screen_resolution: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  user_id?: string;
  // 🎯 NEW: User information extracted from journey URLs
  userInfo?: {
    userId: number;
    name: string | null;
    email: string | null;
    phoneNumber: string | null;
    totalOrders?: number;
    lastOrderDate?: Date;
    isUnknownUser?: boolean;
    multipleUserIds?: number[];
    multipleUsersDetected?: boolean;
    // 💰 Rich Customer Analytics
    customerMetrics?: {
      daysSinceJoined: number;
      daysSinceLastOrder: number | null;
      isNewCustomer: boolean;
      isReturningCustomer: boolean;
      isVIPCustomer: boolean;
      isAtRiskCustomer: boolean;
      customerSegment: string;
      estimatedLifetimeValue: number;
      avgOrderValue: number;
      monthlyOrderFrequency: number;
      customerAge: string;
      totalSpent: number;
    };
  };
  journey: JourneyStep[];
}

const SessionJourneyModal = ({
  isOpen,
  onClose,
  sessionId,
  companyId,
  dataSourceId,
}: SessionJourneyModalProps) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch session details
  const {
    data: sessionData,
    isLoading,
    error,
  } = useQuery<{ data: SessionDetail }>({
    queryKey: ['session-journey', sessionId, companyId, dataSourceId],
    queryFn: async () => {
      if (!sessionId) throw new Error('No session ID provided');

      const response = await api.get(`/api/v1/analytics/sessions/${sessionId}`, {
        params: { companyId, dataSourceId },
      });
      return response.data;
    },
    enabled: isOpen && !!sessionId && !!companyId && !!dataSourceId,
  });

  const session = sessionData?.data;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getDeviceIcon = (device: string) => {
    const deviceLower = device?.toLowerCase() || '';
    if (
      deviceLower.includes('mobile') ||
      deviceLower.includes('iphone') ||
      deviceLower.includes('android')
    ) {
      return <Smartphone className="w-5 h-5 text-blue-500" />;
    }
    if (deviceLower.includes('tablet') || deviceLower.includes('ipad')) {
      return <Tablet className="w-5 h-5 text-purple-500" />;
    }
    return <Monitor className="w-5 h-5 text-green-500" />;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const formatUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname + urlObj.search;
    } catch {
      return url;
    }
  };

  // Mobile Drawer
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
          onClick={onClose}
        />
        {/* Drawer */}
        <div className="fixed inset-x-0 bottom-0 top-16 bg-white dark:bg-secondary-900 z-50 rounded-t-2xl shadow-2xl animate-slide-up overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-secondary-900 border-b border-secondary-200 dark:border-secondary-700 p-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-secondary-900 dark:text-secondary-50">
              Session Journey
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {isLoading && (
              <div className="space-y-4">
                {[1, 2, 3, 4].map(i => (
                  <div
                    key={i}
                    className="h-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded-lg"
                  />
                ))}
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <p className="text-red-500">Failed to load session details</p>
                <button onClick={onClose} className="btn btn-secondary mt-4">
                  Close
                </button>
              </div>
            )}

            {session && (
              <div className="space-y-4">
                {/* Session Info Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="card p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-blue-500" />
                      <span className="text-xs text-secondary-600 dark:text-secondary-400">
                        Duration
                      </span>
                    </div>
                    <p className="text-lg font-bold text-secondary-900 dark:text-secondary-50">
                      {formatDuration(session.duration_seconds)}
                    </p>
                  </div>

                  <div className="card p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-secondary-600 dark:text-secondary-400">
                        Pages
                      </span>
                    </div>
                    <p className="text-lg font-bold text-secondary-900 dark:text-secondary-50">
                      {session.total_pages}
                    </p>
                  </div>

                  <div className="card p-3">
                    <div className="flex items-center gap-2 mb-1">
                      {getDeviceIcon(session.device_type)}
                      <span className="text-xs text-secondary-600 dark:text-secondary-400">
                        Device
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-50">
                      {session.device_type?.split(' ')[0] || 'Unknown'}
                    </p>
                  </div>

                  <div className="card p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Tag className="w-4 h-4 text-purple-500" />
                      <span className="text-xs text-secondary-600 dark:text-secondary-400">
                        Source
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-50 truncate">
                      {session.utm_source || 'Direct'}
                    </p>
                  </div>
                </div>

                {/* Technical Details */}
                <div className="card p-4">
                  <h4 className="font-semibold text-secondary-900 dark:text-secondary-50 mb-3">
                    Technical Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-secondary-600 dark:text-secondary-400">Browser:</span>
                      <span className="font-medium text-secondary-900 dark:text-secondary-50">
                        {session.browser || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600 dark:text-secondary-400">OS:</span>
                      <span className="font-medium text-secondary-900 dark:text-secondary-50">
                        {session.os || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600 dark:text-secondary-400">Resolution:</span>
                      <span className="font-medium text-secondary-900 dark:text-secondary-50">
                        {session.screen_resolution || 'Unknown'}
                      </span>
                    </div>
                    {session.utm_campaign && (
                      <div className="flex justify-between">
                        <span className="text-secondary-600 dark:text-secondary-400">Campaign:</span>
                        <span className="font-medium text-secondary-900 dark:text-secondary-50">
                          {session.utm_campaign}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* User Journey */}
                <div className="card p-4">
                  <h4 className="font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
                    User Journey
                  </h4>
                  <div className="space-y-4">
                    {session.journey.map((step, index) => (
                      <div key={index} className="relative">
                        {/* Timeline connector */}
                        {index < session.journey.length - 1 && (
                          <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-secondary-200 dark:bg-secondary-700" />
                        )}

                        <div className="flex gap-3">
                          {/* Step number */}
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center text-xs font-bold z-10">
                            {index + 1}
                          </div>

                          {/* Step details */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-secondary-900 dark:text-secondary-50 break-words">
                              {step.page_title || formatUrl(step.page_url)}
                            </p>
                            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1 break-all">
                              {formatUrl(step.page_url)}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-secondary-500">
                              <span>
                                {formatDistanceToNow(new Date(step.timestamp), {
                                  addSuffix: true,
                                })}
                              </span>
                              {step.time_on_page_seconds !== null && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDuration(step.time_on_page_seconds)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // Desktop Modal
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white dark:bg-secondary-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-scale-in"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-secondary-200 dark:border-secondary-700 p-6 flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                Session Journey
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1 font-mono">
                {sessionId}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-6">
            {isLoading && (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div
                    key={i}
                    className="h-24 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded-lg"
                  />
                ))}
              </div>
            )}

            {error && (
              <div className="text-center py-12">
                <p className="text-red-500 text-lg">Failed to load session details</p>
                <button onClick={onClose} className="btn btn-secondary mt-4">
                  Close
                </button>
              </div>
            )}

            {session && (
              <div className="space-y-6">
                {/* Session Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="card card-hover p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-secondary-600 dark:text-secondary-400">
                          Duration
                        </p>
                        <p className="text-xl font-bold text-secondary-900 dark:text-secondary-50">
                          {formatDuration(session.duration_seconds)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card card-hover p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-secondary-600 dark:text-secondary-400">
                          Total Pages
                        </p>
                        <p className="text-xl font-bold text-secondary-900 dark:text-secondary-50">
                          {session.total_pages}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card card-hover p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        {getDeviceIcon(session.device_type)}
                      </div>
                      <div>
                        <p className="text-xs text-secondary-600 dark:text-secondary-400">
                          Device
                        </p>
                        <p className="text-lg font-semibold text-secondary-900 dark:text-secondary-50">
                          {session.device_type?.split(' ')[0] || 'Unknown'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card card-hover p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                        <Tag className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs text-secondary-600 dark:text-secondary-400">
                          Source
                        </p>
                        <p className="text-lg font-semibold text-secondary-900 dark:text-secondary-50 truncate">
                          {session.utm_source || 'Direct'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* User Information (from Shop 1) */}
                {session.userInfo && (
                  <div className="card p-6">
                    <h4 className="text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-indigo-600" />
                      Customer Information (Shop 1)
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="flex-1">
                          {session.userInfo.name ? (
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <p className="text-lg font-semibold text-secondary-900 dark:text-secondary-50">
                                  {session.userInfo.name}
                                </p>
                                <a
                                  href={`https://quickmeds.co.uk/dash/users/${session.userInfo.userId}/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-xs font-medium hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                                  title={`Open user ${session.userInfo.userId} in QuickMeds dashboard`}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Open in Dashboard
                                </a>
                              </div>
                              {session.userInfo.email && (
                                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                                  {session.userInfo.email}
                                </p>
                              )}
                              {session.userInfo.phoneNumber && (
                                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                                  {session.userInfo.phoneNumber}
                                </p>
                              )}
                              {session.userInfo.totalOrders !== undefined && session.userInfo.totalOrders > 0 && (
                                <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                                  {session.userInfo.totalOrders} orders placed
                                </p>
                              )}
                              
                              {/* Customer Metrics Badges */}
                              {session.userInfo.customerMetrics && (
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    session.userInfo.customerMetrics.customerSegment === 'VIP' 
                                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                      : session.userInfo.customerMetrics.customerSegment === 'At Risk'
                                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                      : session.userInfo.customerMetrics.customerSegment === 'Returning'
                                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                  }`}>
                                    {session.userInfo.customerMetrics.customerSegment} Customer
                                  </span>
                                  <span className="px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                                    {session.userInfo.customerMetrics.customerAge}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : session.userInfo.isUnknownUser ? (
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <p className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                                  User ID {session.userInfo.userId}
                                </p>
                                <a
                                  href={`https://quickmeds.co.uk/dash/users/${session.userInfo.userId}/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                                  title={`Open user ${session.userInfo.userId} in QuickMeds dashboard`}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Open in Dashboard
                                </a>
                              </div>
                              <p className="text-sm text-amber-600 dark:text-amber-400">
                                Found in URL but not in customer database
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {session.userInfo.multipleUsersDetected && (
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                            <Tag className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              Multiple users detected in this session
                            </span>
                          </div>
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                            User IDs found: {session.userInfo.multipleUserIds?.join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Customer Analytics & Profitability */}
                {session.userInfo?.customerMetrics && (
                  <div className="card p-6">
                    <h4 className="text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      Customer Analytics & Profitability
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Lifetime Value */}
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-green-800 dark:text-green-200">Estimated LTV</span>
                        </div>
                        <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                          £{session.userInfo.customerMetrics.totalSpent || session.userInfo.customerMetrics.estimatedLifetimeValue}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400">
                          {session.userInfo.totalOrders} orders × £{session.userInfo.customerMetrics.avgOrderValue} avg
                        </p>
                      </div>

                      {/* Customer Age */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Customer Since</span>
                        </div>
                        <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
                          {session.userInfo.customerMetrics.daysSinceJoined} days
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          {session.userInfo.customerMetrics.customerAge}
                        </p>
                      </div>

                      {/* Order Frequency */}
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-purple-800 dark:text-purple-200">Activity</span>
                        </div>
                        <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                          {session.userInfo.customerMetrics.monthlyOrderFrequency}/month
                        </p>
                        <p className="text-xs text-purple-600 dark:text-purple-400">
                          Order frequency
                        </p>
                      </div>

                      {/* Last Order */}
                      {session.userInfo.lastOrderDate && (
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="w-4 h-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Last Order</span>
                          </div>
                          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {session.userInfo.customerMetrics.daysSinceLastOrder} days ago
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {new Date(session.userInfo.lastOrderDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}

                      {/* Customer Status */}
                      <div className={`rounded-lg p-4 ${
                        session.userInfo.customerMetrics.customerSegment === 'VIP' 
                          ? 'bg-purple-50 dark:bg-purple-900/20'
                          : session.userInfo.customerMetrics.customerSegment === 'At Risk'
                          ? 'bg-red-50 dark:bg-red-900/20'
                          : session.userInfo.customerMetrics.customerSegment === 'Returning'
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : 'bg-green-50 dark:bg-green-900/20'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Status</span>
                        </div>
                        <p className={`text-lg font-bold ${
                          session.userInfo.customerMetrics.customerSegment === 'VIP' 
                            ? 'text-purple-900 dark:text-purple-100'
                            : session.userInfo.customerMetrics.customerSegment === 'At Risk'
                            ? 'text-red-900 dark:text-red-100'
                            : session.userInfo.customerMetrics.customerSegment === 'Returning'
                            ? 'text-blue-900 dark:text-blue-100'
                            : 'text-green-900 dark:text-green-100'
                        }`}>
                          {session.userInfo.customerMetrics.customerSegment}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {session.userInfo.customerMetrics.isNewCustomer && <span>New</span>}
                          {session.userInfo.customerMetrics.isReturningCustomer && <span>Returning</span>}
                          {session.userInfo.customerMetrics.isVIPCustomer && <span>VIP</span>}
                          {session.userInfo.customerMetrics.isAtRiskCustomer && <span>At Risk</span>}
                        </div>
                      </div>

                      {/* Profitability Indicator */}
                      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Value Tier</span>
                        </div>
                        <p className={`text-lg font-bold ${
                          (session.userInfo.customerMetrics.totalSpent || session.userInfo.customerMetrics.estimatedLifetimeValue) >= 500 
                            ? 'text-green-900 dark:text-green-100'
                            : (session.userInfo.customerMetrics.totalSpent || session.userInfo.customerMetrics.estimatedLifetimeValue) >= 200
                            ? 'text-amber-900 dark:text-amber-100'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {(session.userInfo.customerMetrics.totalSpent || session.userInfo.customerMetrics.estimatedLifetimeValue) >= 500 ? 'High Value' :
                           (session.userInfo.customerMetrics.totalSpent || session.userInfo.customerMetrics.estimatedLifetimeValue) >= 200 ? 'Medium Value' : 'Standard'}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          Based on LTV
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Technical Details */}
                <div className="card p-6">
                  <h4 className="text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
                    Technical Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <Globe className="w-5 h-5 text-blue-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-secondary-600 dark:text-secondary-400">
                          Browser
                        </p>
                        <p className="font-medium text-secondary-900 dark:text-secondary-50">
                          {session.browser || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Monitor className="w-5 h-5 text-green-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-secondary-600 dark:text-secondary-400">
                          Operating System
                        </p>
                        <p className="font-medium text-secondary-900 dark:text-secondary-50">
                          {session.os || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-purple-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-secondary-600 dark:text-secondary-400">
                          Screen Resolution
                        </p>
                        <p className="font-medium text-secondary-900 dark:text-secondary-50">
                          {session.screen_resolution || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    {session.utm_campaign && (
                      <div className="flex items-start gap-3">
                        <Tag className="w-5 h-5 text-orange-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-secondary-600 dark:text-secondary-400">
                            Campaign
                          </p>
                          <p className="font-medium text-secondary-900 dark:text-secondary-50">
                            {session.utm_campaign}
                          </p>
                        </div>
                      </div>
                    )}
                    {session.utm_medium && (
                      <div className="flex items-start gap-3">
                        <Tag className="w-5 h-5 text-pink-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-secondary-600 dark:text-secondary-400">
                            Medium
                          </p>
                          <p className="font-medium text-secondary-900 dark:text-secondary-50">
                            {session.utm_medium}
                          </p>
                        </div>
                      </div>
                    )}
                    {session.user_id && (
                      <div className="flex items-start gap-3">
                        <User className="w-5 h-5 text-indigo-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-secondary-600 dark:text-secondary-400">
                            User ID
                          </p>
                          <p className="font-medium text-secondary-900 dark:text-secondary-50 font-mono text-xs">
                            {session.user_id}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* User Journey Timeline */}
                <div className="card p-6">
                  <h4 className="text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-6">
                    User Journey ({session.total_pages} pages)
                  </h4>
                  <div className="space-y-6">
                    {session.journey.map((step, index) => (
                      <div key={index} className="relative">
                        {/* Timeline connector */}
                        {index < session.journey.length - 1 && (
                          <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-secondary-200 dark:bg-secondary-700" />
                        )}

                        <div className="flex gap-4">
                          {/* Step number */}
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center text-sm font-bold z-10 shadow-md">
                            {index + 1}
                          </div>

                          {/* Step details */}
                          <div className="flex-1 bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <h5 className="font-semibold text-secondary-900 dark:text-secondary-50 mb-1">
                                  {step.page_title || 'Untitled Page'}
                                </h5>
                                <p className="text-sm text-secondary-600 dark:text-secondary-400 break-all">
                                  {formatUrl(step.page_url)}
                                </p>
                                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                                  <div className="flex items-center gap-1.5 text-secondary-600 dark:text-secondary-400">
                                    <Calendar className="w-4 h-4" />
                                    <span>
                                      {formatDistanceToNow(new Date(step.timestamp), {
                                        addSuffix: true,
                                      })}
                                    </span>
                                  </div>
                                  {step.time_on_page_seconds !== null && (
                                    <div className="flex items-center gap-1.5 text-secondary-600 dark:text-secondary-400">
                                      <Clock className="w-4 h-4" />
                                      <span>
                                        Time on page: {formatDuration(step.time_on_page_seconds)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <a
                                href={step.page_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0 p-2 rounded-lg hover:bg-secondary-200 dark:hover:bg-secondary-700 transition-colors"
                                title="Open page"
                              >
                                <ExternalLink className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SessionJourneyModal;

